import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown, Loader2, HelpCircle, ExternalLink } from 'lucide-react';

const emptyForm = { question: '', answer: '', sort_order: 0, is_active: true };

export default function FAQManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/faqs');
      setItems(res.data.faqs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat FAQ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: (items[items.length - 1]?.sort_order ?? items.length - 1) + 1 });
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      question: it.question || '',
      answer: it.answer || '',
      sort_order: it.sort_order ?? 0,
      is_active: it.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('Pertanyaan dan jawaban wajib diisi');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/faqs/${editingId}`, form);
        toast.success('FAQ diperbarui');
      } else {
        await api.post('/admin/faqs', form);
        toast.success('FAQ ditambahkan');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus FAQ "${it.question}"?`)) return;
    try {
      await api.delete(`/admin/faqs/${it.id}`);
      toast.success('FAQ dihapus');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/faqs/${it.id}`, {
        question: it.question,
        answer: it.answer,
        sort_order: it.sort_order,
        is_active: !it.is_active,
      });
      fetchItems();
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    try {
      await Promise.all([
        api.put(`/admin/faqs/${a.id}`, { question: a.question, answer: a.answer, sort_order: b.sort_order, is_active: a.is_active }),
        api.put(`/admin/faqs/${b.id}`, { question: b.question, answer: b.answer, sort_order: a.sort_order, is_active: b.is_active }),
      ]);
      fetchItems();
    } catch {
      toast.error('Gagal mengubah urutan');
    }
  };

  return (
    <div data-testid="admin-faq-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kelola FAQ</h1>
          <p className="text-sm text-slate-500 mt-1">FAQ yang tampil di halaman beranda Tokomoe (section "Pertanyaan yang Sering Diajukan").</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href="/#faq" target="_blank" rel="noopener noreferrer"><ExternalLink size={14} className="mr-1.5" />Lihat di Landing</a>
          </Button>
          <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-faq-btn">
            <Plus size={16} className="mr-1.5" /> Tambah FAQ
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center text-slate-500"><Loader2 size={20} className="animate-spin mr-2" /> Memuat...</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-16 text-center">
            <HelpCircle size={32} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-500 mb-4">Belum ada FAQ. Tambahkan pertanyaan pertama Anda.</p>
            <Button onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"><Plus size={16} className="mr-1.5" /> Tambah FAQ</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <Card key={it.id} className={`border-slate-200 ${!it.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className="flex flex-col gap-1 pt-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik"><ArrowUp size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Turun"><ArrowDown size={14} /></Button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{it.question}</p>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.answer}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-2 mr-2">
                    <Switch checked={it.is_active} onCheckedChange={() => toggleActive(it)} aria-label="Aktif" />
                    <span className="text-xs text-slate-500 hidden md:inline">{it.is_active ? 'Aktif' : 'Nonaktif'}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} aria-label="Edit"><Edit2 size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(it)} aria-label="Hapus" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit FAQ' : 'Tambah FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Pertanyaan</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))}
                placeholder="Contoh: Apakah Tokomoe benar-benar gratis?"
                className="mt-1"
                data-testid="faq-question-input"
              />
            </div>
            <div>
              <Label>Jawaban</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))}
                placeholder="Tulis jawaban yang jelas dan mudah dipahami calon pengguna."
                rows={5}
                className="mt-1"
                data-testid="faq-answer-input"
              />
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 pt-3">
              <div>
                <Label>Tampilkan di Landing</Label>
                <p className="text-xs text-slate-500">Nonaktifkan untuk menyembunyikan tanpa menghapus.</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="faq-save-btn">
              {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Menyimpan...</> : editingId ? 'Simpan Perubahan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
